import { useEffect, useRef } from 'react';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { useFormHasHadDataSetAtLeastOnceContext } from '../../event-state/form-has-had-data-set-at-least-once';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import { usePhoneCallForEvent } from './get-phone-call-for-event';
import { useCallImport } from './use-call-import';
import { shouldAutoImportCallerInfoOnE911 } from './auto-import-caller-info-helpers';

/**
 * When the "Automatically import caller name and location on E911 calls" department
 * preference is enabled and a draft event panel opens from an incoming E911 call,
 * auto-populate the reporting party's name + phone from the ANI/ALI caller data —
 * once, without the calltaker pressing Import All.
 *
 * The E911-origin signal is the workspace panel's `originIsE911` state, which the
 * phone-calls provider sets only when it opens a panel from an incoming E911 call.
 * (The form's `eventOrigin911` boolean is NOT reliable — the backend sets the 911
 * event-origin attribute but never that boolean on the returned form, which is why
 * use-should-refresh-e911-calls also avoids trusting it alone.) Manual event creation
 * never sets `originIsE911`, so that workflow is unaffected regardless of the preference.
 *
 * The import is gated on `useFormHasHadDataSetAtLeastOnceContext()` so it lands *after*
 * the panel's deferred initial `eventForm.reset(...)` (see `useIntegratedEventState`).
 * That reset fires ~a tick after `metadata.eventPhoneCall` synchronously resolves the
 * call, and it re-seeds `reportingParties` from the backend draft view — so a reporting
 * party imported before the reset would be discarded. Gating on the post-reset signal
 * makes the import land after it, so the value survives. See
 * `shouldAutoImportCallerInfoOnE911` for the full rationale.
 *
 * Scope: reporting-party fields only. Location auto-resolution (reverse/forward
 * geocode, auto-pick, spinner/blurb) is handled separately.
 *
 * Mounted once per panel (via useEventPanelFormSideEffects) so it fires a single time
 * on open, independent of which tab is active.
 */
export function useAutoImportCallerInfoOnE911(): void {
    const { autoImportAniAliOnE911Call } = useEnabledFeatures();
    const eventExists = !!useEventMetadataStore()?.state.metadata?.eventId;
    const originIsE911 = !!useWorkspacePanel<'EVENT'>().getPanelState('originIsE911');
    const isFormInitialized = useFormHasHadDataSetAtLeastOnceContext(); // only true after the panel's initial reset, so the import lands after it and isn't wiped
    const selectedCall = usePhoneCallForEvent();
    const { importReportingPartyName, importReportingPartyPhoneNumber } = useCallImport(
        eventExists
    );

    const hasImportedRef = useRef(false);

    useEffect(
        function autoImportReportingPartyFromE911Call() {
            const shouldImport = shouldAutoImportCallerInfoOnE911({
                isPreferenceEnabled: autoImportAniAliOnE911Call,
                isE911Origin: originIsE911,
                eventExists,
                hasSelectedCall: !!selectedCall,
                isFormInitialized,
            });
            if (hasImportedRef.current || !shouldImport || !selectedCall) {
                return;
            }
            hasImportedRef.current = true;
            importReportingPartyName(selectedCall);
            importReportingPartyPhoneNumber(selectedCall);
        },
        [
            autoImportAniAliOnE911Call,
            originIsE911,
            eventExists,
            isFormInitialized,
            selectedCall,
            importReportingPartyName,
            importReportingPartyPhoneNumber,
        ]
    );
}
